import React from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  IconButton,
  Button,
  useDisclosure,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, AddIcon } from "@chakra-ui/icons";
import { useNudgeTemplatesQuery, useDeleteNudgeTemplate } from "../hooks";
import { NudgeTemplate } from "../types";
import NudgeTemplateModal from "./NudgeTemplateModal";

export default function NudgeTemplatesList() {
  const { data: templates, isLoading, error } = useNudgeTemplatesQuery();
  const deleteTemplate = useDeleteNudgeTemplate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedTemplate, setSelectedTemplate] = React.useState<NudgeTemplate | null>(null);

  const handleEdit = (template: NudgeTemplate) => {
    setSelectedTemplate(template);
    onOpen();
  };

  const handleCreate = () => {
    setSelectedTemplate(null);
    onOpen();
  };

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this template?")) {
      await deleteTemplate.mutateAsync(id);
    }
  };

  if (isLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        Error loading nudge templates
      </Alert>
    );
  }

  const systemTemplates = templates?.filter((t) => t.systemNudge) || [];
  const customTemplates = templates?.filter((t) => !t.systemNudge) || [];

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between">
        <Heading size="md">Nudge Templates</Heading>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleCreate}>
          Create Template
        </Button>
      </HStack>

      {systemTemplates.length > 0 && (
        <Box>
          <Heading size="sm" mb={3}>System Templates</Heading>
          <VStack spacing={3} align="stretch">
            {systemTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </VStack>
        </Box>
      )}

      {customTemplates.length > 0 && (
        <Box>
          <Heading size="sm" mb={3}>Custom Templates</Heading>
          <VStack spacing={3} align="stretch">
            {customTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </VStack>
        </Box>
      )}

      {templates?.length === 0 && (
        <Box p={8} textAlign="center" borderWidth={1} borderRadius="md" borderStyle="dashed">
          <Text color="gray.500">No nudge templates yet</Text>
          <Button mt={4} leftIcon={<AddIcon />} colorScheme="blue" onClick={handleCreate}>
            Create Your First Template
          </Button>
        </Box>
      )}

      <NudgeTemplateModal
        isOpen={isOpen}
        onClose={onClose}
        template={selectedTemplate}
      />
    </VStack>
  );
}

interface TemplateCardProps {
  template: NudgeTemplate;
  onEdit: (template: NudgeTemplate) => void;
  onDelete: (id: string) => void;
}

function TemplateCard({ template, onEdit, onDelete }: TemplateCardProps) {
  const nudgeTypeLabels: Record<string, string> = {
    weekly_milestone_digest: "Weekly Digest",
    milestone_reminder: "Milestone Reminder",
    day_of_prompt: "Day-of Prompt",
    task_overdue: "Task Overdue",
    journey_stuck: "Journey Stuck",
  };

  return (
    <Box p={4} borderWidth={1} borderRadius="md" bg="white">
      <HStack justify="space-between">
        <VStack align="start" spacing={1} flex={1}>
          <HStack>
            <Text fontWeight="bold">{template.name}</Text>
            {template.systemNudge && (
              <Badge colorScheme="purple">System</Badge>
            )}
            {!template.active && (
              <Badge colorScheme="gray">Inactive</Badge>
            )}
          </HStack>
          <Text fontSize="sm" color="gray.600">
            {nudgeTypeLabels[template.nudgeType] || template.nudgeType}
          </Text>
          {template.description && (
            <Text fontSize="sm" color="gray.500">
              {template.description}
            </Text>
          )}
        </VStack>
        <HStack>
          {template.editable && (
            <>
              <IconButton
                aria-label="Edit template"
                icon={<EditIcon />}
                size="sm"
                onClick={() => onEdit(template)}
              />
              <IconButton
                aria-label="Delete template"
                icon={<DeleteIcon />}
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={() => onDelete(template.id)}
              />
            </>
          )}
        </HStack>
      </HStack>
    </Box>
  );
}
